import {
  Account,
  AssetType,
  ProductFamily,
  Rates,
  RateSheetFee,
  RateSheetProducts,
} from '../../../../../types/api';
import { Installer } from '../installer';

const SOLAR_PROJECT_FAMILIES: ReadonlyArray<ProductFamily> = [
  ProductFamily.STANDARD,
  ProductFamily.SPECIAL_PAYMENT,
  ProductFamily.ELECTIVE_PAY,
  ProductFamily.TAX_CREDIT,
];

const FLEX_PROJECT_FAMILIES: ReadonlyArray<ProductFamily> = [
  ProductFamily.FLEX,
];

/**
 * Map the asset types funded by the project to the loan product families
 * that should be offered.
 *
 * - Solar selected (alone or with non-solar): Solar product families
 * - EV Chargers selected (alone or with other non-solar): Solar product families
 * - Only storage selected: Solar product families
 * - Other non-solar selected: Flex product families
 * - Nothing selected: Solar product families (default for legacy callers)
 */
const forAssetTypes = (
  selectedAssetTypes: ReadonlyArray<AssetType> = []
): ReadonlyArray<ProductFamily> => {
  if (selectedAssetTypes.length === 0) return SOLAR_PROJECT_FAMILIES;
  // If only storage is selected, return the solar product families
  if (
    selectedAssetTypes.includes(AssetType.ENERGY_STORAGE_BESS) &&
    selectedAssetTypes.length === 1
  ) {
    // For SOL (Storage Only) contractors, offer the solar products
    return SOLAR_PROJECT_FAMILIES;
  }
  if (
    selectedAssetTypes.includes(AssetType.SOLAR) ||
    selectedAssetTypes.includes(AssetType.EV_CHARGERS)
  ) {
    return SOLAR_PROJECT_FAMILIES;
  }
  return FLEX_PROJECT_FAMILIES;
};

/**
 * Returns the rate-sheet product families visible to a given account.
 * - Solar / SOL (or unset): Standard + Special Payment
 * - Flex: Flex only
 * - Solar + Flex: Standard + Special Payment + Flex
 */
const rateSheetFamiliesForAccount = (
  account: Account | null | undefined
): ReadonlyArray<ProductFamily> => {
  const families: ProductFamily[] = [];
  if (!account?.contractor_type || Installer.isContractorTypeEnergy(account)) {
    families.push(ProductFamily.STANDARD, ProductFamily.SPECIAL_PAYMENT);
  }
  if (Installer.hasFlex(account)) {
    families.push(ProductFamily.FLEX);
  }

  // If unknown contractor type, default to Solar families to avoid blank tabs.
  if (families.length === 0) {
    families.push(ProductFamily.STANDARD, ProductFamily.SPECIAL_PAYMENT);
  }
  return families;
};

export const ProductFamilies = {
  forAssetTypes,
  rateSheetFamiliesForAccount,
};

export const getInitialProductFamily = (
  account: Account | null | undefined,
  rates: Rates,
  allowedFamilies?: ReadonlyArray<ProductFamily>
): RateSheetFee['product_family'] => {
  const available = RateSheetProducts.filter(
    (family) =>
      (rates.current[family]?.length ?? 0) > 0 &&
      (!allowedFamilies || allowedFamilies.includes(family))
  );
  if (Installer.isFlexOnly(account) && available.includes(ProductFamily.FLEX)) {
    return ProductFamily.FLEX;
  }
  return available[0] ?? ProductFamily.STANDARD;
};
